import { Component, Pipe, PipeTransform } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import * as v from '../version';
import { DialogTitleComponent } from './comps/dialog-title/dialog-title.component';
import { MaterialModule } from './material.module';

@Pipe({ name: 'urlHost', standalone: true })
export class UrlHostPipe implements PipeTransform {
  transform(url: string): string {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  }
}

interface Attribution {
  name: string;
  url: string;
  description: string;
}

@Component({
    styles: [`
      .about-header {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 16px 0 8px;
        gap: 12px;
      }
      .about-logo {
        height: 48px;
      }
      .about-version {
        font-size: 13px;
        opacity: 0.7;
        letter-spacing: 0.5px;
      }
      .about-tagline {
        font-size: 14px;
        opacity: 0.85;
        text-align: center;
      }
      .about-maker {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        opacity: 0.7;
        text-align: center;
      }
      .section-title {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 1px;
        text-transform: uppercase;
        opacity: 0.6;
        margin: 16px 0 8px;
      }
      .tech-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 8px;
      }
      .tech-chip {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 4px 10px;
        border-radius: 16px;
        border: 1px solid;
        font-size: 12px;
        border-color: color-mix(in srgb, currentColor 30%, transparent);
        opacity: 0.85;
      }
      .attributions-scroll {
        max-height: 220px;
        overflow-y: auto;
        padding-right: 6px;
        scrollbar-width: thin;
      }
      .attributions-list {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .attribution-row {
        display: flex;
        align-items: baseline;
        gap: 8px;
        font-size: 13px;
      }
      .attribution-name {
        font-weight: 500;
        min-width: 160px;
      }
      .attribution-desc {
        opacity: 0.65;
        font-size: 12px;
      }
      .attribution-link {
        font-size: 11px;
        opacity: 0.55;
        margin-left: auto;
        text-decoration: none;
        white-space: nowrap;
      }
      .attribution-link:hover {
        opacity: 1;
        text-decoration: underline;
      }
      .copyright {
        font-size: 12px;
        opacity: 0.55;
        text-align: center;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid;
        border-color: color-mix(in srgb, currentColor 15%, transparent);
      }
    `],
    template: `
  <app-dialog-title [title]="title" icon="info"></app-dialog-title>
  <div mat-dialog-content style="min-width:460px; max-width:520px;" data-cy="about-dialog-content">

    <div class="about-header">
      <img class="about-logo" data-cy="about-logo" src="assets/barXuiBBlue.svg" alt="barxui logo" />
      <div class="about-version" data-cy="about-version">Version {{ version }}</div>
      <div class="about-tagline" data-cy="about-tagline">Cloud label design &amp; printing solutions.</div>
      <div class="about-maker" data-cy="about-maker">
        <mat-icon style="font-size:16px; width:16px; height:16px;">business</mat-icon>
        Created &amp; maintained by <a href="https://hexcom.com/" target="_blank" rel="noopener noreferrer"><strong>HEXCOM Ltd.</strong></a>
        <mat-icon style="font-size:16px; width:16px; height:16px;">location_on</mat-icon>
        New Zealand
      </div>
    </div>

    <div class="section-title">Built with</div>
    <div class="tech-chips">
      @for (t of techStack; track t.label) {
        <span class="tech-chip">
          <mat-icon style="font-size:14px; width:14px; height:14px;">{{ t.icon }}</mat-icon>
          {{ t.label }}
        </span>
      }
    </div>

    <div class="section-title">Open Source Attribution</div>
    <div class="attributions-scroll" data-cy="about-attributions">
      <div class="attributions-list">
        @for (a of attributions; track a.name) {
          <div class="attribution-row">
            <span class="attribution-name">{{ a.name }}</span>
            <span class="attribution-desc">{{ a.description }}</span>
            <a class="attribution-link" [href]="a.url" target="_blank" rel="noopener noreferrer">{{ a.url | urlHost }}</a>
          </div>
        }
      </div>
    </div>

    <div class="copyright">&copy; {{ year }} HEXCOM Ltd. All rights reserved.</div>
  </div>
  <div mat-dialog-actions align="end">
    <button type="button" mat-raised-button color="primary" (click)="goAhead()"><mat-icon>done</mat-icon> OK</button>
  </div>
  `,
    imports: [MaterialModule, DialogTitleComponent, UrlHostPipe]
})
export class AboutDialogComponent {
  title = 'About barxui';
  version = v.version.version;
  year = new Date().getFullYear();

  techStack = [
    { label: 'Angular 21', icon: 'web' },
    { label: 'Angular Material', icon: 'palette' },
    { label: 'TypeScript', icon: 'code' },
    { label: 'RxJS', icon: 'sync' },
    { label: 'Progressive Web App', icon: 'install_mobile' },
  ];

  attributions: Attribution[] = [
    { name: 'AG Grid',              url: 'https://www.ag-grid.com',                        description: 'High-performance data grid' },
    { name: 'Angular Material',     url: 'https://material.angular.io',                    description: 'UI component library' },
    { name: 'Angular CDK',          url: 'https://material.angular.io/cdk',                description: 'Component Dev Kit primitives' },
    { name: 'Angular Flex Layout',  url: 'https://github.com/angular/flex-layout',         description: 'Responsive flex layout directives' },
    { name: 'AWS Amplify',          url: 'https://aws.amazon.com/amplify',                 description: 'Authentication & cloud services' },
    { name: 'buffer',               url: 'https://github.com/feross/buffer',               description: 'Node.js Buffer API for browsers' },
    { name: 'core-js',              url: 'https://github.com/zloirock/core-js',            description: 'JavaScript standard library polyfills' },
    { name: 'Fabric.js',            url: 'http://fabricjs.com',                            description: 'Canvas graphics & label designer' },
    { name: 'file-saver',           url: 'https://github.com/eligrey/FileSaver.js',        description: 'Client-side file saving' },
    { name: 'idb-keyval',           url: 'https://github.com/jakearchibald/idb-keyval',   description: 'IndexedDB key-value store' },
    { name: 'Moment.js',            url: 'https://momentjs.com',                           description: 'Date & time formatting' },
    { name: 'ngx-image-cropper',    url: 'https://github.com/Mawi137/ngx-image-cropper',  description: 'Image cropping component' },
    { name: 'ngx-infinite-scroll',  url: 'https://github.com/orizens/ngx-infinite-scroll', description: 'Infinite scroll directive' },
    { name: 'ngx-logger',           url: 'https://github.com/dbfannin/ngx-logger',        description: 'Client-side logging' },
    { name: 'ngx-stripe',           url: 'https://ngx-stripe.dev',                         description: 'Angular wrapper for Stripe.js' },
    { name: 'RxJS',                 url: 'https://rxjs.dev',                               description: 'Reactive extensions for JavaScript' },
    { name: 'Stripe.js',            url: 'https://stripe.com',                             description: 'Payment processing' },
    { name: 'Syncfusion PDF Viewer', url: 'https://www.syncfusion.com',                    description: 'PDF viewer component' },
    { name: 'ts-cacheable',         url: 'https://github.com/angelnikolov/ts-cacheable',  description: 'TypeScript method caching decorator' },
    { name: 'tslib',                url: 'https://github.com/microsoft/tslib',             description: 'TypeScript runtime helpers' },
  ];

  constructor(private dialogRef: MatDialogRef<AboutDialogComponent>) {
    this.dialogRef?.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });
  }

  goAhead() {
    this.dialogRef.close(null);
  }
}
